import React, { useEffect, useMemo, useState } from 'react';
import PageLayout from '../components/pagelayout';
import { supabase } from '../supabase';
import './schedulingManagement.css';

export default function SchedulingManagement() {
  const [settings, setSettings] = useState({
    exhibition_start_date: '',
    exhibition_end_date: '',
    booking_open_date: '',
    booking_close_date: '',
    exhibition_active: true,
    booking_enabled: true,
    max_tickets_per_booking: 10,
  });

  const [contents, setContents] = useState([]);
  const [events, setEvents] = useState([]);
  const [message, setMessage] = useState('');
  const [editingContentId, setEditingContentId] = useState(null);

  const [contentForm, setContentForm] = useState({
    title: '',
    type: 'Banner',
    scheduled_date: '',
    scheduled_time: '',
    subtitle: '',
    year: '',
    location: '',
    time_text: '',
    button_text: '',
    button_url: '',
  });

  const [eventForm, setEventForm] = useState({
    title: '',
    event_date: '',
    event_time: '',
    duration: '',
    capacity: '',
  });

  const loadData = async () => {
    const { data: settingsData } = await supabase
      .from('scheduling_settings')
      .select('*')
      .limit(1)
      .single();

    if (settingsData) setSettings(settingsData);

    const { data: contentData } = await supabase
      .from('scheduled_content')
      .select('*')
      .order('scheduled_date', { ascending: true })
      .order('scheduled_time', { ascending: true });

    setContents(contentData || []);

    const { data: eventsData } = await supabase
      .from('scheduled_events')
      .select('*')
      .order('event_date', { ascending: true });

    setEvents(eventsData || []);
  };

  useEffect(() => {
    loadData();

    const channel = supabase
      .channel('scheduling_changes')
      .on('postgres_changes', { event: '*', schema: 'public' }, () => loadData())
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  const bookingStatus = useMemo(() => {
    const today = new Date();
    const open = new Date(settings.booking_open_date);
    const close = new Date(settings.booking_close_date);

    const isOpen =
      settings.booking_enabled &&
      today >= open &&
      today <= close;

    const daysLeft = Math.max(
      0,
      Math.ceil((close - today) / (1000 * 60 * 60 * 24))
    );

    return { isOpen, daysLeft };
  }, [settings]);

  const countdown = useMemo(() => {
    const today = new Date();
    const start = new Date(settings.exhibition_start_date);
    const diff = Math.max(0, start - today);

    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      min: Math.floor((diff / (1000 * 60)) % 60),
    };
  }, [settings.exhibition_start_date]);

  const updateSettings = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const saveSettings = async () => {
    const { error } = await supabase
      .from('scheduling_settings')
      .upsert({
        ...settings,
        updated_at: new Date().toISOString(),
      });

    if (error) {
      setMessage('Save failed: ' + error.message);
    } else {
      setMessage('Settings saved successfully.');
      loadData();
    }
  };

  const resetContentForm = () => {
    setEditingContentId(null);
    setContentForm({
      title: '',
      type: 'Banner',
      scheduled_date: '',
      scheduled_time: '',
      subtitle: '',
      year: '',
      location: '',
      time_text: '',
      button_text: '',
      button_url: '',
    });
  };

  const editContent = (item) => {
    setEditingContentId(item.id);

    setContentForm({
      title: item.title || '',
      type: item.type || 'Banner',
      scheduled_date: item.scheduled_date || '',
      scheduled_time: item.scheduled_time || '',
      subtitle: item.subtitle || '',
      year: item.year || '',
      location: item.location || '',
      time_text: item.time_text || '',
      button_text: item.button_text || '',
      button_url: item.button_url || '',
    });
  };

  const saveContent = async () => {
    if (!contentForm.title) {
      setMessage('Please add content title.');
      return;
    }

    const payload = {
      ...contentForm,
      status: 'Scheduled',
    };

    let result;

    if (editingContentId) {
      result = await supabase
        .from('scheduled_content')
        .update(payload)
        .eq('id', editingContentId);
    } else {
      result = await supabase
        .from('scheduled_content')
        .insert(payload);
    }

    if (result.error) {
      setMessage('Content save failed: ' + result.error.message);
    } else {
      setMessage(editingContentId ? 'Content updated successfully.' : 'Content scheduled successfully.');
      resetContentForm();
      loadData();
    }
  };

  const deleteContent = async (id) => {
    await supabase.from('scheduled_content').delete().eq('id', id);
    loadData();
  };

  const addEvent = async () => {
    if (!eventForm.title) {
      setMessage('Please add event title.');
      return;
    }

    const { error } = await supabase
      .from('scheduled_events')
      .insert({
        ...eventForm,
        capacity: Number(eventForm.capacity),
      });

    if (error) {
      setMessage('Event save failed: ' + error.message);
    } else {
      setMessage('Event added successfully.');
      setEventForm({
        title: '',
        event_date: '',
        event_time: '',
        duration: '',
        capacity: '',
      });
      loadData();
    }
  };

  const deleteEvent = async (id) => {
    await supabase.from('scheduled_events').delete().eq('id', id);
    loadData();
  };

  return (
    <PageLayout activePage="scheduling">
      <div className="schedulingPage">
        <div className="schedulingHeader">
          <h1>Scheduling Management</h1>
          <p>Control exhibition dates and scheduled content</p>
        </div>

        {message && <div className="scheduleMessage">{message}</div>}

        <div className="settingsGrid">
          <section className="schedulePanel">
            <h2>Exhibition Settings</h2>

            <label>
              Exhibition Start Date
              <input
                type="date"
                value={settings.exhibition_start_date || ''}
                onChange={(e) => updateSettings('exhibition_start_date', e.target.value)}
              />
            </label>

            <label>
              Exhibition End Date
              <input
                type="date"
                value={settings.exhibition_end_date || ''}
                onChange={(e) => updateSettings('exhibition_end_date', e.target.value)}
              />
            </label>

            <div className="toggleRow">
              <div>
                <h4>Exhibition Active</h4>
                <p>Enable or disable exhibition</p>
              </div>
              <input
                type="checkbox"
                checked={!!settings.exhibition_active}
                onChange={(e) => updateSettings('exhibition_active', e.target.checked)}
              />
            </div>

            <div className="countdownBox">
              <h4>Countdown Timer</h4>
              <p>Countdown automatically displayed on website</p>

              <div className="countdownGrid">
                <span><strong>{countdown.days}</strong>days</span>
                <span><strong>{countdown.hours}</strong>hours</span>
                <span><strong>{countdown.min}</strong>min</span>
              </div>
            </div>
          </section>

          <section className="schedulePanel">
            <h2>Booking Settings</h2>

            <label>
              Booking Opens From
              <input
                type="date"
                value={settings.booking_open_date || ''}
                onChange={(e) => updateSettings('booking_open_date', e.target.value)}
              />
            </label>

            <label>
              Booking Closes On
              <input
                type="date"
                value={settings.booking_close_date || ''}
                onChange={(e) => updateSettings('booking_close_date', e.target.value)}
              />
            </label>

            <div className="toggleRow">
              <div>
                <h4>Booking Open</h4>
                <p>Allow new bookings</p>
              </div>
              <input
                type="checkbox"
                checked={!!settings.booking_enabled}
                onChange={(e) => updateSettings('booking_enabled', e.target.checked)}
              />
            </div>

            <div className={bookingStatus.isOpen ? 'bookingOpenBox' : 'bookingClosedBox'}>
              {bookingStatus.isOpen ? (
                <>
                  <strong>Booking is currently open</strong>
                  <p>{bookingStatus.daysLeft} days remaining until close</p>
                </>
              ) : (
                <>
                  <strong>Booking is currently closed</strong>
                  <p>Booking button will be hidden on the website</p>
                </>
              )}
            </div>

            <label>
              Max Tickets Per Booking
              <input
                type="number"
                value={settings.max_tickets_per_booking || 1}
                onChange={(e) =>
                  updateSettings('max_tickets_per_booking', Number(e.target.value))
                }
              />
            </label>
          </section>
        </div>

        <button className="saveScheduleBtn" onClick={saveSettings}>
          Save Settings
        </button>

        <section className="fullPanel">
          <div className="panelTop">
            <h2>Scheduled Content</h2>
          </div>

          <div className="miniForm contentScheduleForm">
            <input
              placeholder="Title"
              value={contentForm.title}
              onChange={(e) =>
                setContentForm({ ...contentForm, title: e.target.value })
              }
            />

            <select
              value={contentForm.type}
              onChange={(e) =>
                setContentForm({ ...contentForm, type: e.target.value })
              }
            >
              <option>Banner</option>
              <option>Popup</option>
              <option>Notification</option>
            </select>

            <input
              type="date"
              value={contentForm.scheduled_date}
              onChange={(e) =>
                setContentForm({ ...contentForm, scheduled_date: e.target.value })
              }
            />

            <input
              type="time"
              value={contentForm.scheduled_time}
              onChange={(e) =>
                setContentForm({ ...contentForm, scheduled_time: e.target.value })
              }
            />

            {contentForm.type === 'Popup' && (
              <>
                <input
                  placeholder="Popup subtitle"
                  value={contentForm.subtitle}
                  onChange={(e) =>
                    setContentForm({ ...contentForm, subtitle: e.target.value })
                  }
                />

                <input
                  placeholder="Year"
                  value={contentForm.year}
                  onChange={(e) =>
                    setContentForm({ ...contentForm, year: e.target.value })
                  }
                />

                <input
                  placeholder="Location"
                  value={contentForm.location}
                  onChange={(e) =>
                    setContentForm({ ...contentForm, location: e.target.value })
                  }
                />

                <input
                  placeholder="Time text"
                  value={contentForm.time_text}
                  onChange={(e) =>
                    setContentForm({ ...contentForm, time_text: e.target.value })
                  }
                />

                <input
                  placeholder="Button text"
                  value={contentForm.button_text}
                  onChange={(e) =>
                    setContentForm({ ...contentForm, button_text: e.target.value })
                  }
                />

                <input
                  placeholder="Button URL"
                  value={contentForm.button_url}
                  onChange={(e) =>
                    setContentForm({ ...contentForm, button_url: e.target.value })
                  }
                />
              </>
            )}

            <button type="button" onClick={saveContent}>
              {editingContentId ? 'Save Content Changes' : '+ Schedule Content'}
            </button>

            {editingContentId && (
              <button type="button" className="cancelScheduleBtn" onClick={resetContentForm}>
                Cancel Edit
              </button>
            )}
          </div>

          {contents.map((item) => (
            <div className="contentRow" key={item.id}>
              <div>
                <h4>{item.title}</h4>
                <p>{item.scheduled_date} · {item.scheduled_time}</p>
                {item.type === 'Popup' && (
                  <p>{item.subtitle} · {item.location}</p>
                )}
              </div>

              <div>
                <span>{item.type}</span>
                <span>{item.status}</span>

                <button type="button" onClick={() => editContent(item)}>
                  Edit
                </button>

                <button type="button" onClick={() => deleteContent(item.id)}>
                  🗑
                </button>
              </div>
            </div>
          ))}
        </section>

        <section className="fullPanel">
          <div className="panelTop">
            <h2>Event Schedule</h2>
          </div>

          <div className="miniForm">
            <input
              placeholder="Event title"
              value={eventForm.title}
              onChange={(e) =>
                setEventForm({ ...eventForm, title: e.target.value })
              }
            />

            <input
              type="date"
              value={eventForm.event_date}
              onChange={(e) =>
                setEventForm({ ...eventForm, event_date: e.target.value })
              }
            />

            <input
              type="time"
              value={eventForm.event_time}
              onChange={(e) =>
                setEventForm({ ...eventForm, event_time: e.target.value })
              }
            />

            <input
              placeholder="Duration"
              value={eventForm.duration}
              onChange={(e) =>
                setEventForm({ ...eventForm, duration: e.target.value })
              }
            />

            <input
              type="number"
              placeholder="Capacity"
              value={eventForm.capacity}
              onChange={(e) =>
                setEventForm({ ...eventForm, capacity: e.target.value })
              }
            />

            <button type="button" onClick={addEvent}>
              + Add Event
            </button>
          </div>

          <div className="eventsGrid">
            {events.map((event) => (
              <div className="eventCard" key={event.id}>
                <h4>{event.title}</h4>
                <p>📅 {event.event_date}</p>
                <p>🕘 {event.event_time} ({event.duration})</p>
                <p>Capacity: {event.capacity}</p>

                <button type="button">Edit</button>
                <button type="button" onClick={() => deleteEvent(event.id)}>🗑</button>
              </div>
            ))}
          </div>
        </section>
      </div>
    </PageLayout>
  );
}